import * as React from 'react';

import { ContentSection, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $brokeredOrderForm,
  BrokeredOrderFormState,
} from '../../_state/brokeredOrderForm.state';
import { useTranslations } from '../../i18n/useTranslations';

import { PurchaseOrdersMultiSelect } from '~/modules/trade/_components/_shared/PurchaseOrdersMultiSelect';
import { SalesOrdersMultiSelect } from '~/modules/trade/_components/_shared/SalesOrdersMultiSelect';

type BrokeredOrderWizardStepContractInfoProps = {
  disabled?: boolean;
};

const formSelector = (state: BrokeredOrderFormState) => ({
  purchaseOrderIds: state.purchaseOrderIds,
  salesOrderIds: state.salesOrderIds,
  notes: state.notes,
});

const $contractInfoForm = selectAtom($brokeredOrderForm, formSelector, isEqual);

export const BrokeredOrderWizardStepContractInfo: React.FC<
  BrokeredOrderWizardStepContractInfoProps
> = ({ disabled = false }) => {
  const t = useTranslations();

  const form = useAtomValue($contractInfoForm);
  const updateForm = useSetAtom($brokeredOrderForm);

  const handleFieldChange = React.useCallback(
    <K extends keyof BrokeredOrderFormState>(field: K) =>
      (value: BrokeredOrderFormState[K]) => {
        updateForm((prev) => ({
          ...prev,
          [field]: value,
        }));
      },
    [updateForm],
  );

  return (
    <div className="@BrokeredOrderWizardStepContractInfo space-y-6">
      <ContentSection title={t.orders.detail.tabs.linkedOrders} variant="panel">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <PurchaseOrdersMultiSelect
              label={t.orders.detail.linkedOrders.purchaseOrders}
              selectedEntries={form.purchaseOrderIds.map((id) => ({
                id,
                documentNumber: '',
              }))}
              onSelectionChange={(entries) =>
                handleFieldChange('purchaseOrderIds')(entries.map((e) => e.id))
              }
              placeholder={t.orders.link.searchPlaceholder}
              isDisabled={disabled}
            />
          </div>
          <div>
            <SalesOrdersMultiSelect
              label={t.orders.detail.linkedOrders.salesOrders}
              selectedEntries={form.salesOrderIds.map((id) => ({
                id,
                documentNumber: '',
              }))}
              onSelectionChange={(entries) =>
                handleFieldChange('salesOrderIds')(entries.map((e) => e.id))
              }
              placeholder={t.orders.link.searchPlaceholder}
              isDisabled={disabled}
            />
          </div>
        </div>
      </ContentSection>

      <ContentSection
        title={t.common.sections.additionalInfo}
        variant="panel"
        hideDivider
      >
        <TextField
          label={t.common.labels.notes}
          value={form.notes}
          onChange={(value) => handleFieldChange('notes')(value)}
          placeholder={t.common.placeholders.optionalNotes}
          textArea
          isDisabled={disabled}
        />
      </ContentSection>
    </div>
  );
};
